import config from 'constants/config'
import { createSelector } from 'reselect'
import { isEnabledForFeature, isFeatureFlagIncluded } from 'lib/config/featureFlagUtils'
import { Experiments } from 'constants/experiments'
import { isHeadlessModeSel, isCurrentDeviceMobile } from './configSelectors'
import memoize from 'lib/memoize/memoize'
import { getFlightItems } from 'checkout/selectors/view/flights'
import { isVillaOffer, isLEOffer, isBedbankOffer, isLEHotel } from 'lib/offer/offerTypes'
import { MERCHANT_FEE_ENABLED_REGIONS, TRAVEL_PROTECTION_VALID_COUNTRIES } from 'constants/config/region'
import { isBedbankRefundable } from './bedbankSelectors'
import { getCartHasPropertyCollectItem } from 'checkout/selectors/payment/propertyCollectSelectors'
import getCheckoutTotalsView from 'checkout/selectors/payment/getCheckoutTotalsView'
import { isBannerHiddenForPath } from 'lib/routing/routeVisibility'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import { isPaidSessionSelector } from './marketingSelectors'
import { isSpoofed } from './isSpoofed'
import { isExperimentPreview } from 'lib/experiments/preview'

export function shouldEnableCalendarRecommendations(offer: App.Offer | App.BedbankOffer | undefined) {
  return !isVillaOffer(offer) && (isLEOffer(offer) || isBedbankOffer(offer))
}

export const isCustomerSupportMode = createSelector(
  (state: App.State) => isSpoofed(state),
  (state: App.State) => state.system.previewMode,
  (state: App.State) => state.system.storeMode,
  (isSpoofed, isPreviewMode, isStoreMode) => isSpoofed || isPreviewMode || isStoreMode,
)

export const shouldShowSalesUSP = createSelector(
  (_state: App.State, lowestPricedPackageTrackingPrice: number, threshold: number) => lowestPricedPackageTrackingPrice >= threshold,
  (state: App.State) => state.geo.currentRegionCode,
  (isPriceOverThreshold, currentRegion): boolean => isEnabledForFeature('ALL/IN', currentRegion) && isPriceOverThreshold,
)

export const getIsMultiRoomEnabled = createSelector(
  (_state: App.State, offer: App.AnyOffer | undefined) => offer?.type,
  (state: App.State, offer: App.AnyOffer | undefined) => isLEHotel(offer) ? !!state.offer.propertyOfferMappings[offer.id]?.mapping : undefined,
  (offerType, hasPropertyMapping): boolean => {
    // We don't want the complexity of multi room booking on property pages (TAO offers that have a mapping)
    const isLPCWithPropertyMapping = offerType === 'tactical_ao_hotel' && !!hasPropertyMapping

    return !!offerType &&
      config.MULTIPLE_ROOM_TYPE_BOOKING_ENABLED &&
      !isLPCWithPropertyMapping &&
      isEnabledForFeature(config.MULTIPLE_ROOM_TYPE_BOOKING_ENABLED_LIST, offerType)
  },
)

export const shouldShowContinueToApp = createSelector(
  (state: App.State) => isCurrentDeviceMobile(state),
  (state: App.State) => isHeadlessModeSel(state),
  (state: App.State) => state.system.appBannerCookie,
  (state: App.State) => state.utm.medium,
  (state: App.State) => !!state.router.location.query.gclid,
  (state: App.State) => state.system.originalCanonicalURL,
  (state: App.State) => state.router.location.pathname,
  (state: App.State) => state.utm.content,
  (state: App.State) => isPaidSessionSelector(state),
  (
    isMobileDevice,
    isHeadlessMode,
    appBannerCookie,
    utmMedium,
    hasGoogleClickIdentifier,
    url,
    pathname,
    utmContent,
    isPaidSession,
  ): boolean => {
    return config.CONTINUE_TO_APP_BANNER &&
      isMobileDevice &&
      !isHeadlessMode &&
      !appBannerCookie &&
      !(utmMedium === 'cpc' || hasGoogleClickIdentifier) &&
      (utmMedium !== 'cpc' && !(utmContent === 'gha-lpp' || utmContent === 'gha-lpc')) &&
      !url.includes('top-picks-for-me') &&
      !isBannerHiddenForPath(pathname, 'appInstallBanner') &&
      !isPaidSession
  },
)

export const isVelocityEnabled = (_state: App.State): boolean => config.VIRGIN_VELOCITY_ENABLED && !!config.VELOCITY_SSO_URL

export const isManualSortingEnabledFor = memoize((listName: string) => {
  return isEnabledForFeature(config.MANUAL_SORTING_ENABLED, listName)
})

export const isSupportAssistantEnabled = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.optimizely.optimizelyExperiments?.[Experiments.isSupportAssistantEnabled.id],
  (state: App.State) => isExperimentPreview(state),
  (currentRegionCode, supportAssistantFlag, preview) => {
    return !preview && supportAssistantFlag?.variationKey === 'on' &&
      config.SUPPORT_ASSISTANT_ENABLED &&
      isFeatureFlagIncluded(config.SUPPORT_ASSISTANT_REGIONS, currentRegionCode)
  },
)

export const isMerchantFeeEnabled = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (currentRegionCode) => {
    if (!config.MERCHANT_FEE_ENABLED) {
      return false
    }

    // Enable for Agent Hub in all regions
    if (config.agentHub.isEnabled) {
      return true
    }

    if (!MERCHANT_FEE_ENABLED_REGIONS.includes(currentRegionCode)) {
      return false
    }

    return true
  },
)

export const isMerchantFeeFlightsEnabled = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => isSpoofed(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getFlightItems(state),
  (currentRegionCode, isSpoofed, postPurchase, flightItems) => {
    if (!config.MERCHANT_FEE_ENABLED) {
      return false
    }

    if (!MERCHANT_FEE_ENABLED_REGIONS.includes(currentRegionCode)) {
      return false
    }

    // Disable fees for postpurchase (except charter upgrades which support payment fees)
    if (postPurchase && postPurchase !== 'charter-upgrade') {
      return false
    }

    if (isSpoofed) {
      return true
    }

    if (config.BRAND !== 'luxuryescapes') {
      return true
    }

    // enable merchant fee for bundled and standalone flights
    return !!(flightItems?.length)
  },
)

export const isMerchantFeeAvailable = createSelector(
  isMerchantFeeEnabled,
  getFlightItems,
  isBedbankRefundable,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => getCartHasPropertyCollectItem(state),
  (state: App.State) => getCheckoutTotalsView(state).data.payableNow,
  (isMerchantFeeEnabled, flightItems, isBedbankRefundable, postPurchase, existingOrder, cartHasPropertyCollectItem, payableNow) => {
    // For pay-at-property carts with add-ons payable now, merchant fee should still apply.
    if (isMerchantFeeEnabled && cartHasPropertyCollectItem && payableNow > 0) {
      return true
    }

    // For post-purchase custom offers with merchant fees enabled, only check region eligibility
    // Charter upgrades also support payment fees
    if (postPurchase && postPurchase !== 'charter-upgrade' && postPurchase !== 'esim-upgrade') {
      const customOfferMerchantFeeEnabled = existingOrder?.customOfferItems?.[0]?.custom_offer?.payment_metadata?.merchant_fee_enabled
      if (customOfferMerchantFeeEnabled) {
        // Custom offer has merchant fees enabled - only check region eligibility
        return isMerchantFeeEnabled
      }
      return false
    }

    // Disable fees if LPP if room is refundable
    if (isBedbankRefundable) {
      return false
    }

    // Disable if flight items
    if (flightItems?.length) {
      return false
    }

    return isMerchantFeeEnabled
  },
)

export const isTravelProtectionAvailableInRegion = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (currentRegionCode): boolean => {
    // New travel protection policies are only available in AU/NZ for now
    return TRAVEL_PROTECTION_VALID_COUNTRIES.includes(currentRegionCode)
  },
)

export const isCharterEnabled = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (regionCode): boolean => {
    return regionCode === 'AU'
  },
)
